import httpClient from '../functions/httpClient';

// Matches the backend's ALLOWED_ATTACHMENT_MIME_TYPES + RECORDING_MIME_TYPES
// (ProductFeedbackController.ts) — kept as extension hints here since `accept` matches more
// reliably by extension across browsers/OSes.
export const FEEDBACK_ATTACHMENT_ACCEPT =
  'image/*,.pdf,.csv,.txt,.json,.zip,.xls,.xlsx,.doc,.docx,.mp4,.mov,.webm,.ogg,.mp3,.wav,.m4a';
export const FEEDBACK_ATTACHMENT_HELP_TEXT =
  'Images, PDF, CSV, TXT, JSON, ZIP, Excel, Word (10MB each), or a short recording (70MB) — max 10 files.';

export interface FeedbackAttachment {
  url: string;
  name: string;
  available: boolean;
}

/** An attachment that saved to the ticket but couldn't be synced to the GitHub issue. */
export interface FeedbackAttachmentFailure {
  name: string;
  reason: string;
}

export interface ProductFeedback {
  _id: string;
  ticketId: string;
  type: 'bug' | 'enhancement' | 'feature';
  title: string;
  description: string;
  priority?: 'high' | 'medium' | 'low';
  section?: string;
  affectedUrl?: string;
  images: FeedbackAttachment[];
  status: 'open' | 'solved';
  githubIssueNumber?: number;
  githubIssueUrl?: string;
  orgName?: string;
  userName?: string;
  userEmail?: string;
  createdAt: string;
  updatedAt: string;
}

export interface FeedbackListResponse {
  success: boolean;
  data: ProductFeedback[];
  total: number;
  page: number;
  limit: number;
}

export interface AllFeedbackFilters {
  orgId?: string;
  type?: string;
  priority?: string;
  status?: string;
  dateFrom?: string;
  dateTo?: string;
}

export type OrgFeedbackFilters = Omit<AllFeedbackFilters, 'orgId'>;

export const submitFeedback = (payload: FormData): Promise<any> =>
  httpClient('/product-feedback', { method: 'POST', body: payload }) as Promise<any>;

export interface InlineImageUploadResponse {
  success: boolean;
  data: {
    /** Public, unsigned blob storage URL — safe to render in an <img> and to embed in the
     *  markdown as-is; it also renders directly in the mirrored GitHub issue body. */
    url: string;
    name: string;
  };
  message?: string;
}

/**
 * Uploads one image pasted/dropped into a description editor and returns a URL to embed
 * as markdown. Separate from the `images` attachment list — these live inside the
 * description text, not the attachment chips.
 */
export const uploadInlineDescriptionImage = (file: File): Promise<InlineImageUploadResponse> => {
  const formData = new FormData();
  formData.append('image', file);
  return httpClient('/product-feedback/inline-image', {
    method: 'POST',
    body: formData,
  }) as Promise<InlineImageUploadResponse>;
};

export const fetchMyFeedback = (page = 1, limit = 20): Promise<FeedbackListResponse> =>
  httpClient(`/product-feedback/me?page=${page}&limit=${limit}`) as Promise<FeedbackListResponse>;

export const fetchAllFeedback = (
  filters: AllFeedbackFilters = {},
  page = 1,
  limit = 20
): Promise<FeedbackListResponse> => {
  const params = new URLSearchParams();
  if (filters.orgId) params.set('orgId', filters.orgId);
  if (filters.type) params.set('type', filters.type);
  if (filters.priority) params.set('priority', filters.priority);
  if (filters.status) params.set('status', filters.status);
  if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
  if (filters.dateTo) params.set('dateTo', filters.dateTo);
  params.set('page', String(page));
  params.set('limit', String(limit));
  return httpClient(`/product-feedback?${params.toString()}`) as Promise<FeedbackListResponse>;
};

export const fetchOrgFeedback = (
  filters: OrgFeedbackFilters = {},
  page = 1,
  limit = 20
): Promise<FeedbackListResponse> => {
  const params = new URLSearchParams();
  if (filters.type) params.set('type', filters.type);
  if (filters.priority) params.set('priority', filters.priority);
  if (filters.status) params.set('status', filters.status);
  if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
  if (filters.dateTo) params.set('dateTo', filters.dateTo);
  params.set('page', String(page));
  params.set('limit', String(limit));
  return httpClient(`/product-feedback/org?${params.toString()}`) as Promise<FeedbackListResponse>;
};

export const updateFeedback = (id: string, payload: FormData): Promise<any> =>
  httpClient(`/product-feedback/${id}`, { method: 'PATCH', body: payload }) as Promise<any>;

export const solveTicket = (id: string): Promise<any> =>
  httpClient(`/product-feedback/${id}/solve`, { method: 'PATCH' }) as Promise<any>;

export const deleteTicket = (id: string): Promise<any> =>
  httpClient(`/product-feedback/${id}`, { method: 'DELETE' }) as Promise<any>;